import React, { useState, useEffect } from "react";
import { supabase } from "../supabase";

export default function ScheduleSection() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase
        .from("schedule")
        .select("*")
        .order("game_date")
        .order("game_time");
      if (data) setGames(data);
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading || games.length === 0) return null;

  const today = new Date().toISOString().split("T")[0];

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    return {
      weekday: d.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase(),
      month: d.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
      day: d.getDate(),
    };
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [h, m] = timeStr.split(":");
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const display = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${display}:${m} ${ampm}`;
  };

  // Split into upcoming and past
  const upcoming = games.filter((g) => g.game_date >= today);
  const past = games.filter((g) => g.game_date < today);

  return (
    <section className="mb-10">
      <h2 className="font-display text-2xl font-bold text-navy mb-5 text-center">
        2026 Schedule
      </h2>

      {/* Upcoming games */}
      <div className="space-y-2">
        {upcoming.map((g) => {
          const date = formatDate(g.game_date);
          return (
            <div
              key={g.id}
              className="bg-white rounded-lg border border-warm-gray p-4 flex items-center gap-4"
            >
              {/* Date block */}
              <div className="w-14 text-center shrink-0">
                <p className="text-[10px] font-bold text-red-600 tracking-wider">
                  {date.month}
                </p>
                <p className="text-2xl font-bold text-navy leading-none">{date.day}</p>
                <p className="text-[10px] text-navy/40 font-medium">{date.weekday}</p>
              </div>

              {/* Game info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-navy text-sm">
                    vs {g.opponent}
                  </span>
                  {g.is_home && (
                    <span className="text-[10px] font-bold text-green-700 bg-green-100 px-1.5 py-0.5 rounded">
                      HOME
                    </span>
                  )}
                </div>
                <p className="text-xs text-navy/50 truncate mt-0.5">
                  {formatTime(g.game_time)} &middot; {g.location}
                </p>
                {g.notes && (
                  <p className="text-xs text-red-600/70 mt-0.5">{g.notes}</p>
                )}
              </div>

              {/* Result or upcoming indicator */}
              <div className="shrink-0">
                {g.result ? (
                  <span
                    className={`text-sm font-bold ${
                      g.result.startsWith("W")
                        ? "text-green-600"
                        : g.result.startsWith("L")
                        ? "text-red-500"
                        : "text-navy/60"
                    }`}
                  >
                    {g.result}
                  </span>
                ) : (
                  <span className="text-xs text-navy/30 font-medium">Upcoming</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Past games (collapsed) */}
      {past.length > 0 && (
        <PastGames games={past} formatDate={formatDate} formatTime={formatTime} />
      )}
    </section>
  );
}

function PastGames({ games, formatDate, formatTime }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mt-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="text-sm text-navy/50 hover:text-navy font-medium flex items-center gap-1 mx-auto"
      >
        {expanded ? "Hide" : "Show"} past games ({games.length})
        <svg
          className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expanded && (
        <div className="space-y-2 mt-3">
          {games.map((g) => {
            const date = formatDate(g.game_date);
            return (
              <div
                key={g.id}
                className="bg-warm-gray/40 rounded-lg p-3 flex items-center gap-4 opacity-70"
              >
                <div className="w-14 text-center shrink-0">
                  <p className="text-[10px] font-bold text-navy/40 tracking-wider">
                    {date.month}
                  </p>
                  <p className="text-xl font-bold text-navy/60 leading-none">{date.day}</p>
                </div>
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-navy/70 text-sm">vs {g.opponent}</span>
                  <p className="text-xs text-navy/40 truncate">
                    {formatTime(g.game_time)} &middot; {g.location}
                  </p>
                </div>
                {g.result && (
                  <span
                    className={`text-sm font-bold ${
                      g.result.startsWith("W")
                        ? "text-green-600"
                        : g.result.startsWith("L")
                        ? "text-red-500"
                        : "text-navy/40"
                    }`}
                  >
                    {g.result}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
